<template>
  <main class="quiz-host-page">
    <div class="host-ambient" aria-hidden="true"></div>

    <header class="host-header">
      <button type="button" class="host-icon-button" aria-label="Quiz verlassen" @click="handleExit">
        <v-icon>mdi-arrow-left</v-icon>
      </button>
      <div class="host-brand">
        <img :src="brandLogo" alt="WELL COME FIT" width="1200" height="931" />
        <div><strong>LIVE QUIZ</strong><small>Leiteransicht</small></div>
      </div>
      <div class="host-actions">
        <span class="connection-pill" :class="{ offline: !connected }"><i></i>{{ connected ? "Synchron" : "Verbinde" }}</span>
        <button
          v-if="showPauseControl"
          type="button"
          class="host-pause-button"
          :class="{ active: snapshot.state === 'paused' || snapshot.pauseRequested }"
          :aria-label="pauseActionLabel"
          :disabled="actionPending"
          @click="togglePause">
          <v-icon>{{ pauseActionIcon }}</v-icon><span>{{ pauseActionLabel }}</span>
        </button>
        <button type="button" class="host-icon-button" :aria-label="soundEnabled ? 'Musik ausschalten' : 'Musik einschalten'" @click="toggleSound">
          <v-icon>{{ soundEnabled ? "mdi-volume-high" : "mdi-volume-off" }}</v-icon>
        </button>
        <button type="button" class="host-icon-button" :aria-label="fullscreen ? 'Vollbild verlassen' : 'Vollbild öffnen'" @click="toggleFullscreen">
          <v-icon>{{ fullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen" }}</v-icon>
        </button>
      </div>
    </header>

    <section v-if="joining && !snapshot" class="host-center-state">
      <v-progress-circular indeterminate color="#58d388" size="58" width="4" />
      <h1>Live-Session wird geladen</h1>
      <p>Spielstand und Teilnehmer werden synchronisiert.</p>
    </section>

    <section v-else-if="!snapshot" class="host-center-state error-state">
      <span><v-icon>mdi-alert-circle-outline</v-icon></span>
      <h1>Session nicht erreichbar</h1>
      <p>{{ error || "Diese Quiz-Session konnte nicht geladen werden." }}</p>
      <v-btn color="#58d388" depressed large @click="joinSession">Erneut verbinden</v-btn>
    </section>

    <template v-else>
      <div v-if="error" class="host-error" role="alert">
        <v-icon>mdi-alert-circle-outline</v-icon><span>{{ error }}</span>
        <button type="button" aria-label="Meldung schliessen" @click="clearError"><v-icon>mdi-close</v-icon></button>
      </div>

      <div v-if="snapshot.pauseRequested" class="pause-requested-banner" role="status">
        <span><v-icon>mdi-timer-pause-outline</v-icon></span>
        <div><strong>Pause ist vorgemerkt</strong><small>Die laufende Frage wird fair aufgelöst, danach pausiert das Quiz automatisch.</small></div>
        <button type="button" :disabled="actionPending" @click="togglePause">Pause aufheben</button>
      </div>

      <section v-if="snapshot.state === 'lobby'" class="host-stage host-lobby">
        <div class="lobby-hero">
          <span class="host-kicker"><i></i> LOBBY IST OFFEN</span>
          <h1>{{ snapshot.quizTitle }}</h1>
          <p>Teilnehmer öffnen das <strong>Live Quiz</strong> und geben diesen Code ein:</p>
          <div class="giant-code" aria-label="Quiz-Code">{{ formatCode(snapshot.joinCode) }}</div>
          <div class="lobby-live-count"><span>{{ connectedParticipantCount }}</span><small>{{ connectedParticipantCount === 1 ? "Person ist bereit" : "Personen sind bereit" }}</small></div>
        </div>

        <div class="host-participants">
          <div class="participants-heading"><span>Live in der Lobby</span><strong>{{ snapshot.participantCount }}</strong></div>
          <div v-if="snapshot.participants.length" class="participant-list">
            <div v-for="participant in snapshot.participants" :key="participant.participantId" :class="{ offline: !participant.connected }">
              <span>{{ initials(participant.displayName) }}</span><strong>{{ participant.displayName }}</strong><i></i>
            </div>
          </div>
          <div v-else class="participants-empty"><v-icon>mdi-account-multiple-plus-outline</v-icon><p>Noch wartet niemand in der Lobby.</p></div>

          <v-btn
            color="#58d388"
            class="primary-host-action"
            x-large
            block
            depressed
            :loading="actionPending"
            :disabled="connectedParticipantCount === 0"
            @click="startGame">
            <v-icon left>mdi-play</v-icon>Quiz starten
          </v-btn>
          <small v-if="connectedParticipantCount === 0" class="start-hint">Der Start wird aktiv, sobald die erste Person beigetreten ist.</small>
        </div>
      </section>

      <section v-else-if="snapshot.state === 'countdown'" class="host-stage host-countdown">
        <span class="host-kicker">FRAGE {{ snapshot.currentQuestionIndex + 1 }} VON {{ snapshot.totalQuestions }}</span>
        <div class="countdown-ring" :key="countdownValue"><span>{{ countdownValue }}</span></div>
        <h1>Alle bereit?</h1>
      </section>

      <section v-else-if="snapshot.state === 'question'" class="host-stage host-question">
        <div class="question-status-row">
          <div class="question-context">
            <span>Frage {{ snapshot.currentQuestionIndex + 1 }} / {{ snapshot.totalQuestions }}</span>
            <span class="question-type-pill"><v-icon>{{ questionTypeIcon }}</v-icon>{{ questionTypeLabel }}</span>
          </div>
          <div class="question-live-controls">
            <span class="question-answer-count"><v-icon>mdi-account-check-outline</v-icon>{{ snapshot.answerCount }} / {{ snapshot.participantCount }} geantwortet</span>
            <button
              type="button"
              class="question-pause-action"
              :class="{ active: snapshot.pauseRequested }"
              :aria-label="snapshot.pauseRequested ? 'Vorgemerkte Pause aufheben' : 'Quiz nach dieser Frage pausieren'"
              :disabled="actionPending"
              @click="togglePause">
              <v-icon>{{ snapshot.pauseRequested ? "mdi-play" : "mdi-timer-pause-outline" }}</v-icon>
              <span>{{ snapshot.pauseRequested ? "Vorgemerkte Pause aufheben" : "Nach dieser Frage pausieren" }}</span>
            </button>
          </div>
        </div>
        <div class="host-timer-track"><i :style="{ width: `${questionProgress}%` }"></i></div>
        <div class="host-question-title">
          <h1>{{ snapshot.question.text }}</h1>
          <div class="host-timer" :style="{ '--timer-progress': `${questionProgress * 3.6}deg` }"><strong>{{ remainingSeconds }}</strong><small>Sek.</small></div>
        </div>
        <div v-if="isEstimateQuestion" class="host-estimate-prompt">
          <span><v-icon>mdi-chart-bell-curve-cumulative</v-icon></span>
          <div>
            <small>SCHÄTZUNG LÄUFT</small>
            <strong>{{ estimateRuleText }}</strong>
            <p>
              Die Teilnehmenden geben einen Zahlenwert<span v-if="snapshot.question.unit"> in {{ snapshot.question.unit }}</span> ein.
              Der Zielwert bleibt bis zur Auflösung verborgen.
            </p>
          </div>
          <div class="estimate-live-count">
            <strong>{{ snapshot.answerCount }}</strong>
            <small>{{ Number(snapshot.answerCount) === 1 ? "Schätzung" : "Schätzungen" }}</small>
          </div>
        </div>
        <div v-else class="host-answer-grid">
          <div v-for="(answer, index) in snapshot.question.answers" :key="answer._id" class="host-answer" :class="answerTone(index)">
            <span><v-icon>{{ answerIcon(index) }}</v-icon></span><strong>{{ answer.text }}</strong>
          </div>
        </div>
        <p v-if="isMultipleQuestion" class="question-guidance"><v-icon>mdi-checkbox-multiple-marked-outline</v-icon>Mehrere Antworten können richtig sein.</p>
        <p class="auto-note"><v-icon>mdi-timer-outline</v-icon>Die Antwort wird nach Ablauf der Zeit automatisch aufgelöst.</p>
      </section>

      <section v-else-if="snapshot.state === 'reveal'" class="host-stage host-reveal">
        <span class="host-kicker">AUFLÖSUNG</span>
        <h1>{{ snapshot.question.text }}</h1>
        <div v-if="isEstimateQuestion" class="estimate-reveal">
          <div class="estimate-target-card">
            <span><v-icon>mdi-bullseye-arrow</v-icon></span>
            <div>
              <small>RICHTIGER ZIELWERT</small>
              <strong>{{ formatEstimate(snapshot.question.targetValue) }} <em v-if="snapshot.question.unit">{{ snapshot.question.unit }}</em></strong>
              <p>{{ estimateRuleText }}</p>
            </div>
          </div>

          <div class="estimate-response-panel">
            <div class="estimate-response-heading">
              <div><span>Schätzungen</span><small>Nach Punkten und Nähe sortiert</small></div>
              <strong>{{ estimateResponses.length }}</strong>
            </div>
            <div v-if="estimateResponses.length" class="estimate-response-list">
              <div
                v-for="(response, index) in estimateResponses.slice(0, 10)"
                :key="`${response.participantId}-${index}`"
                :class="{ winner: response.correct }">
                <span class="estimate-rank">{{ index + 1 }}</span>
                <span class="estimate-avatar">{{ initials(response.displayName) }}</span>
                <div class="estimate-person">
                  <strong>{{ response.displayName }}</strong>
                  <small>{{ estimateDistanceText(response) }}</small>
                </div>
                <strong class="estimate-value">{{ formatEstimate(response.value) }} <small v-if="snapshot.question.unit">{{ snapshot.question.unit }}</small></strong>
                <span class="estimate-points">{{ formatScore(response.points) }}</span>
              </div>
              <small v-if="estimateResponses.length > 10" class="estimate-more">+ {{ estimateResponses.length - 10 }} weitere Schätzungen</small>
            </div>
            <div v-else class="estimate-empty"><v-icon>mdi-comment-question-outline</v-icon><span>Für diese Frage wurde keine Schätzung abgegeben.</span></div>
          </div>
        </div>
        <div v-else class="distribution-grid">
          <div
            v-for="(answer, index) in snapshot.question.answers"
            :key="answer._id"
            class="distribution-card"
            :class="[answerTone(index), { correct: isCorrect(answer) }]">
            <div class="distribution-top">
              <span><v-icon>{{ answerIcon(index) }}</v-icon></span>
              <strong>{{ answer.text }}</strong>
              <v-icon v-if="isCorrect(answer)" class="correct-icon">mdi-check-circle</v-icon>
            </div>
            <div class="distribution-bar"><i :style="{ width: `${distributionPercent(answer)}%` }"></i></div>
            <small>{{ answer.responseCount || 0 }} {{ Number(answer.responseCount) === 1 ? "Antwort" : "Antworten" }}</small>
          </div>
        </div>
        <div class="host-bottom-action">
          <span>{{ snapshot.answerCount }} von {{ snapshot.participantCount }} haben geantwortet</span>
          <v-btn color="#58d388" class="primary-host-action compact" x-large depressed :loading="actionPending" @click="nextStage">
            Rangliste anzeigen <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </section>

      <section v-else-if="snapshot.state === 'leaderboard'" class="host-stage host-leaderboard">
        <span class="host-kicker">LIVE-ZWISCHENSTAND</span>
        <h1>Rangliste</h1>
        <div class="host-ranking">
          <div v-for="entry in snapshot.leaderboard.slice(0, 7)" :key="entry.participantId" :class="`rank-${entry.rank}`">
            <span class="rank-number">{{ entry.rank }}</span>
            <span class="rank-avatar">{{ initials(entry.displayName) }}</span>
            <strong>{{ entry.displayName }}</strong>
            <small>{{ entry.correctCount }} richtig</small>
            <span class="rank-score">{{ formatScore(entry.score) }}</span>
          </div>
        </div>
        <div class="leaderboard-actions">
          <button type="button" class="leaderboard-pause-action" :disabled="actionPending" @click="togglePause">
            <v-icon>mdi-pause</v-icon><span>Quiz pausieren</span>
          </button>
          <v-btn color="#58d388" class="primary-host-action compact ranking-action" x-large depressed :loading="actionPending" @click="nextStage">
            {{ isLastQuestion ? "Finale anzeigen" : "Nächste Frage" }} <v-icon right>{{ isLastQuestion ? "mdi-trophy-outline" : "mdi-arrow-right" }}</v-icon>
          </v-btn>
        </div>
      </section>

      <section v-else-if="snapshot.state === 'paused'" class="host-stage host-paused">
        <div class="paused-card">
          <span class="paused-icon"><v-icon>mdi-pause</v-icon></span>
          <span class="host-kicker">QUIZ PAUSIERT</span>
          <h1>Bereit, wenn ihr es seid.</h1>
          <p>Alle Antworten und Punkte sind gespeichert. Die Teilnehmenden können die Seite schliessen und später genau hier weiterfahren.</p>
          <div class="paused-facts">
            <div><span><v-icon>mdi-map-marker-path</v-icon></span><small>Spielstand</small><strong>{{ pausedProgressLabel }}</strong></div>
            <div><span><v-icon>mdi-account-group-outline</v-icon></span><small>Teilnehmende</small><strong>{{ snapshot.participantCount }} gespeichert</strong></div>
            <div><span><v-icon>mdi-clock-outline</v-icon></span><small>Automatisches Ende</small><strong>{{ pauseExpiryLabel }}</strong></div>
          </div>
          <div class="paused-actions">
            <v-btn color="#58d388" class="primary-host-action compact" x-large depressed :loading="actionPending" @click="togglePause">
              <v-icon left>mdi-play</v-icon>Quiz fortsetzen
            </v-btn>
            <v-btn outlined color="#c4cec8" class="paused-secondary-action" x-large @click="returnToManage">
              Später fortsetzen
            </v-btn>
          </div>
        </div>
      </section>

      <section v-else-if="snapshot.state === 'finished'" class="host-stage host-finished">
        <span class="host-kicker">QUIZ ABGESCHLOSSEN</span>
        <h1>Das Siegerpodest</h1>
        <div class="host-podium" :class="{ 'single-winner': snapshot.leaderboard.length === 1 }">
          <div v-if="snapshot.leaderboard[1]" class="host-podium-place place-two">
            <span>{{ initials(snapshot.leaderboard[1].displayName) }}</span><strong>{{ snapshot.leaderboard[1].displayName }}</strong><small>{{ formatScore(snapshot.leaderboard[1].score) }}</small><i>2</i>
          </div>
          <div v-if="snapshot.leaderboard[0]" class="host-podium-place place-one">
            <v-icon>mdi-crown</v-icon><span>{{ initials(snapshot.leaderboard[0].displayName) }}</span><strong>{{ snapshot.leaderboard[0].displayName }}</strong><small>{{ formatScore(snapshot.leaderboard[0].score) }}</small><i>1</i>
          </div>
          <div v-if="snapshot.leaderboard[2]" class="host-podium-place place-three">
            <span>{{ initials(snapshot.leaderboard[2].displayName) }}</span><strong>{{ snapshot.leaderboard[2].displayName }}</strong><small>{{ formatScore(snapshot.leaderboard[2].score) }}</small><i>3</i>
          </div>
        </div>
        <v-btn color="#58d388" class="primary-host-action compact" x-large depressed @click="returnToManage">Zur Quiz-Verwaltung</v-btn>
      </section>

      <section v-else-if="snapshot.state === 'expired'" class="host-center-state error-state">
        <span><v-icon>mdi-clock-alert-outline</v-icon></span>
        <h1>Quiz-Pause abgelaufen</h1>
        <p>Nach zwölf Stunden ohne Fortsetzung wurde diese Runde automatisch geschlossen.</p>
        <v-btn color="#58d388" depressed large @click="returnToManage">Zur Verwaltung</v-btn>
      </section>

      <section v-else class="host-center-state error-state">
        <span><v-icon>mdi-cancel</v-icon></span>
        <h1>Session beendet</h1>
        <p>Diese Quiz-Runde wurde geschlossen.</p>
        <v-btn color="#58d388" depressed large @click="returnToManage">Zur Verwaltung</v-btn>
      </section>
    </template>

    <v-snackbar v-model="audioBlocked" color="#3b2e12" rounded timeout="5000" bottom>
      <v-icon left color="#ffd166">mdi-volume-off</v-icon>Musik wurde vom Gerät blockiert. Tippe oben auf das Lautsprecher-Symbol.
    </v-snackbar>
  </main>
</template>

<script>
import { brandLogo } from "@/constants/brandAssets"
import { mapState } from "vuex"

export default {
  name: "LiveQuizHost",
  data() {
    return {
      brandLogo,
      now: Date.now(),
      clockOffset: 0,
      clockTimer: null,
      soundEnabled: true,
      music: null,
      fadeFrame: null,
      musicQuestionId: null,
      musicStarting: false,
      musicAttempt: 0,
      fullscreen: false,
      audioBlocked: false,
    }
  },
  computed: {
    ...mapState("liveQuizStore", ["snapshot", "connected", "joining", "actionPending", "error"]),
    serverNow() {
      return this.now + this.clockOffset
    },
    countdownValue() {
      if (!this.snapshot || !this.snapshot.countdownEndsAt) return 1
      return Math.max(1, Math.ceil((new Date(this.snapshot.countdownEndsAt).getTime() - this.serverNow) / 1000))
    },
    remainingMs() {
      if (!this.snapshot || !this.snapshot.questionEndsAt) return 0
      return Math.max(0, new Date(this.snapshot.questionEndsAt).getTime() - this.serverNow)
    },
    remainingSeconds() {
      return Math.max(0, Math.ceil(this.remainingMs / 1000))
    },
    questionProgress() {
      if (!this.snapshot || !this.snapshot.questionStartedAt || !this.snapshot.questionEndsAt) return 0
      const start = new Date(this.snapshot.questionStartedAt).getTime()
      const end = new Date(this.snapshot.questionEndsAt).getTime()
      if (end <= start) return 0
      return Math.min(100, Math.max(0, ((end - this.serverNow) / (end - start)) * 100))
    },
    isLastQuestion() {
      return this.snapshot && this.snapshot.currentQuestionIndex >= this.snapshot.totalQuestions - 1
    },
    showPauseControl() {
      return this.snapshot && ["lobby", "countdown", "question", "reveal", "leaderboard", "paused"].includes(this.snapshot.state)
    },
    pauseActionLabel() {
      if (!this.snapshot) return "Quiz pausieren"
      if (this.snapshot.state === "paused") return "Fortsetzen"
      if (this.snapshot.pauseRequested) return "Pause aufheben"
      return "Pausieren"
    },
    pauseActionIcon() {
      return this.snapshot && (this.snapshot.state === "paused" || this.snapshot.pauseRequested) ? "mdi-play" : "mdi-pause"
    },
    pausedProgressLabel() {
      if (!this.snapshot) return "Gespeichert"
      if (this.snapshot.resumeState === "lobby") return "Lobby bleibt offen"
      if (this.snapshot.resumeState === "leaderboard") return `Nach Frage ${this.snapshot.currentQuestionIndex + 1}`
      return `Frage ${this.snapshot.currentQuestionIndex + 1} aufgelöst`
    },
    pauseExpiryLabel() {
      if (!this.snapshot || !this.snapshot.expiresAt) return "In zwölf Stunden"
      const expiresAt = new Date(this.snapshot.expiresAt)
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(today.getDate() + 1)
      const sameDay = (first, second) =>
        first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth() && first.getDate() === second.getDate()
      const time = new Intl.DateTimeFormat("de-CH", { hour: "2-digit", minute: "2-digit" }).format(expiresAt)
      if (sameDay(expiresAt, today)) return `Heute um ${time} Uhr`
      if (sameDay(expiresAt, tomorrow)) return `Morgen um ${time} Uhr`
      return new Intl.DateTimeFormat("de-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(expiresAt)
    },
    connectedParticipantCount() {
      if (!this.snapshot) return 0
      if (this.snapshot.connectedParticipantCount != null && Number.isFinite(Number(this.snapshot.connectedParticipantCount))) {
        return Number(this.snapshot.connectedParticipantCount)
      }
      return (this.snapshot.participants || []).filter((participant) => participant.connected).length
    },
    isMultipleQuestion() {
      return this.snapshot && this.snapshot.question && this.snapshot.question.type === "multiple"
    },
    isEstimateQuestion() {
      return this.snapshot && this.snapshot.question && this.snapshot.question.type === "estimate"
    },
    questionTypeLabel() {
      if (this.isEstimateQuestion) return "Schätzfrage"
      if (this.isMultipleQuestion) return "Mehrfachauswahl"
      return "Einzelwahl"
    },
    questionTypeIcon() {
      if (this.isEstimateQuestion) return "mdi-chart-bell-curve-cumulative"
      if (this.isMultipleQuestion) return "mdi-checkbox-multiple-marked-outline"
      return "mdi-radiobox-marked"
    },
    estimateRuleText() {
      if (!this.isEstimateQuestion) return ""
      if (this.snapshot.question.estimateMode === "tolerance") {
        return `Innerhalb ±${this.formatEstimate(this.snapshot.question.tolerancePercent)} % gibt es 500 bis 1000 Punkte.`
      }
      return "Die nächste Schätzung gewinnt 1000 Punkte. Gleichstände zählen gemeinsam."
    },
    estimateResponses() {
      if (!this.isEstimateQuestion || !Array.isArray(this.snapshot.question.estimateResponses)) return []
      return this.snapshot.question.estimateResponses
    },
  },
  watch: {
    snapshot: {
      immediate: true,
      handler(snapshot) {
        if (!snapshot) return
        if (snapshot.serverNow) this.clockOffset = new Date(snapshot.serverNow).getTime() - Date.now()
        const questionId = snapshot.question ? String(snapshot.question._id) : null
        if (snapshot.state === "question" && this.soundEnabled && snapshot.musicEnabled) {
          if (this.musicQuestionId !== questionId || (this.music && this.music.paused)) {
            this.musicQuestionId = questionId
            this.startMusic()
          }
        } else if (snapshot.state !== "countdown") {
          this.musicQuestionId = null
          this.stopMusic()
        }
      },
    },
  },
  mounted() {
    this.prepareMusic()
    this.clockTimer = setInterval(() => {
      this.now = Date.now()
    }, 100)
    document.addEventListener("fullscreenchange", this.updateFullscreenState)
    this.joinSession()
  },
  beforeDestroy() {
    if (this.clockTimer) clearInterval(this.clockTimer)
    document.removeEventListener("fullscreenchange", this.updateFullscreenState)
    this.destroyMusic()
    this.$store.dispatch("liveQuizStore/leave")
  },
  methods: {
    async joinSession() {
      try {
        await this.$store.dispatch("liveQuizStore/join", {
          sessionId: this.$route.params.sessionId,
          mode: "host",
        })
      } catch (error) {
        console.error("Live-Quiz Host-Verbindung fehlgeschlagen:", error)
      }
    },
    async startGame() {
      await this.unlockAudio()
      try {
        await this.$store.dispatch("liveQuizStore/start")
      } catch (error) {
        console.error("Live-Quiz Start fehlgeschlagen:", error)
      }
    },
    async nextStage() {
      if (this.snapshot && this.snapshot.state === "leaderboard") await this.unlockAudio()
      try {
        await this.$store.dispatch("liveQuizStore/next")
      } catch (error) {
        console.error("Live-Quiz Wechsel fehlgeschlagen:", error)
      }
    },
    async togglePause() {
      if (!this.snapshot || this.actionPending) return
      const action = this.snapshot.state === "paused" || this.snapshot.pauseRequested ? "resume" : "pause"
      try {
        await this.$store.dispatch(`liveQuizStore/${action}`)
      } catch (error) {
        console.error("Live-Quiz Pause konnte nicht geändert werden:", error)
      }
    },
    async handleExit() {
      if (this.snapshot && ["paused", "finished", "cancelled", "expired"].includes(this.snapshot.state)) {
        this.returnToManage()
        return
      }
      if (this.snapshot && this.snapshot.pauseRequested) {
        this.returnToManage()
        return
      }
      if (!window.confirm("Möchtest du diese Live-Session wirklich beenden?")) return
      try {
        await this.$store.dispatch("liveQuizStore/finish", true)
      } catch (error) {
        console.error("Live-Quiz Abbruch fehlgeschlagen:", error)
      }
      this.returnToManage()
    },
    async returnToManage() {
      await this.$store.dispatch("liveQuizStore/leave")
      if (this.$route.name !== "liveQuizManage") this.$router.push({ name: "liveQuizManage" })
    },
    clearError() {
      this.$store.dispatch("liveQuizStore/clearError")
    },
    isCorrect(answer) {
      if (!this.snapshot.question) return false
      const correctAnswerIds = Array.isArray(this.snapshot.question.correctAnswerIds)
        ? this.snapshot.question.correctAnswerIds.map(String)
        : []
      if (correctAnswerIds.length) return correctAnswerIds.includes(String(answer._id))
      return String(this.snapshot.question.correctAnswerId || "") === String(answer._id)
    },
    distributionPercent(answer) {
      if (!this.snapshot.answerCount) return 0
      return Math.min(100, (Number(answer.responseCount || 0) / this.snapshot.answerCount) * 100)
    },
    answerTone(index) {
      return ["tone-coral", "tone-blue", "tone-amber", "tone-violet"][index % 4]
    },
    answerIcon(index) {
      return ["mdi-triangle", "mdi-rhombus", "mdi-circle", "mdi-square"][index % 4]
    },
    initials(name) {
      return String(name || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("") || "?"
    },
    formatCode(code) {
      const value = String(code || "")
      return value.length === 6 ? `${value.slice(0, 3)} ${value.slice(3)}` : value
    },
    formatScore(score) {
      return new Intl.NumberFormat("de-CH").format(Number(score || 0))
    },
    formatEstimate(value) {
      const numericValue = Number(value)
      if (!Number.isFinite(numericValue)) return "–"
      return new Intl.NumberFormat("de-CH", { maximumFractionDigits: 6 }).format(numericValue)
    },
    estimateDistanceText(response) {
      if (!response || response.distance == null) return "Abstand nicht verfügbar"
      const unit = this.snapshot && this.snapshot.question ? this.snapshot.question.unit : ""
      const absoluteDistance = `${this.formatEstimate(response.distance)}${unit ? ` ${unit}` : ""}`
      const percentDistance = response.distancePercent == null ? "" : ` · ${this.formatEstimate(response.distancePercent)} %`
      return `Abstand ${absoluteDistance}${percentDistance}`
    },
    prepareMusic() {
      this.music = new Audio("/music/quizMusic.mp3")
      this.music.loop = true
      this.music.preload = "auto"
      this.music.volume = 0
      this.music.load()
    },
    async unlockAudio() {
      if (!this.music || !this.soundEnabled) return
      const music = this.music
      try {
        music.volume = 0
        await music.play()
      } catch (error) {
        this.audioBlocked = true
        this.soundEnabled = false
      }
    },
    toggleSound() {
      this.soundEnabled = !this.soundEnabled
      if (this.soundEnabled && this.snapshot && this.snapshot.state === "question" && this.snapshot.musicEnabled) {
        this.musicQuestionId = this.snapshot.question ? String(this.snapshot.question._id) : null
        this.startMusic()
      } else if (this.soundEnabled && this.snapshot && this.snapshot.state === "countdown" && this.snapshot.musicEnabled) {
        this.unlockAudio()
      } else {
        this.musicQuestionId = null
        this.stopMusic()
      }
    },
    startMusic() {
      if (!this.music || this.musicStarting) return
      const attempt = ++this.musicAttempt
      this.musicStarting = true
      const syncPlayback = () => {
        if (attempt !== this.musicAttempt) return
        if (this.snapshot && this.snapshot.questionStartedAt && Number.isFinite(this.music.duration) && this.music.duration > 0) {
          const elapsedSeconds = Math.max(0, (this.serverNow - new Date(this.snapshot.questionStartedAt).getTime()) / 1000)
          this.music.currentTime = elapsedSeconds % this.music.duration
        }
      }
      if (this.music.readyState >= 1) syncPlayback()
      else this.music.addEventListener("loadedmetadata", syncPlayback, { once: true })
      this.music.play().then(() => {
        if (
          attempt !== this.musicAttempt ||
          !this.snapshot ||
          this.snapshot.state !== "question" ||
          !this.soundEnabled ||
          !this.snapshot.musicEnabled
        ) {
          this.musicStarting = false
          if (this.music) {
            this.music.pause()
            this.music.volume = 0
          }
          return
        }
        this.musicStarting = false
        this.fadeMusic(0.42, 500)
      }).catch(() => {
        if (attempt !== this.musicAttempt) return
        this.musicStarting = false
        this.audioBlocked = true
        this.soundEnabled = false
      })
    },
    stopMusic() {
      this.musicAttempt += 1
      this.musicStarting = false
      if (!this.music || this.music.paused) return
      this.fadeMusic(0, 350, true)
    },
    fadeMusic(target, duration, pauseAfter = false) {
      if (!this.music) return
      if (this.fadeFrame) cancelAnimationFrame(this.fadeFrame)
      const startedAt = performance.now()
      const startVolume = this.music.volume
      const update = (timestamp) => {
        const progress = Math.min(1, (timestamp - startedAt) / duration)
        this.music.volume = Math.min(1, Math.max(0, startVolume + (target - startVolume) * progress))
        if (progress < 1) this.fadeFrame = requestAnimationFrame(update)
        else if (pauseAfter) this.music.pause()
      }
      this.fadeFrame = requestAnimationFrame(update)
    },
    destroyMusic() {
      this.musicAttempt += 1
      this.musicStarting = false
      if (this.fadeFrame) cancelAnimationFrame(this.fadeFrame)
      if (!this.music) return
      this.music.pause()
      this.music.src = ""
      this.music = null
    },
    async toggleFullscreen() {
      try {
        if (!document.fullscreenElement) await document.documentElement.requestFullscreen()
        else await document.exitFullscreen()
      } catch (error) {
        console.warn("Vollbild konnte nicht umgeschaltet werden:", error)
      }
    },
    updateFullscreenState() {
      this.fullscreen = Boolean(document.fullscreenElement)
    },
  },
}
</script>

<style scoped>
.quiz-host-page {
  --green: #58d388;
  --border: rgba(255, 255, 255, 0.11);
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  color: #f6f9f7;
  background: radial-gradient(circle at 50% -14%, rgba(0, 150, 64, 0.2), transparent 42%), #080b0a;
  font-size: 1rem;
}

.host-ambient { position: fixed; left: 50%; bottom: -28rem; width: 60rem; height: 40rem; border-radius: 50%; background: rgba(88, 211, 136, 0.08); filter: blur(7rem); translate: -50% 0; pointer-events: none; }
.host-header { position: relative; z-index: 5; display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; min-height: 5rem; padding: 0.75rem 1.25rem; border-bottom: 1px solid rgba(255, 255, 255, 0.07); background: rgba(8, 11, 10, 0.74); backdrop-filter: blur(1.25rem); }
.host-icon-button { display: grid; width: 3rem; height: 3rem; place-items: center; border: 1px solid var(--border); border-radius: 1rem; color: #edf2ef; background: rgba(255, 255, 255, 0.045); }
.host-icon-button .v-icon { color: currentColor; }
.host-brand { display: flex; align-items: center; gap: 0.7rem; }
.host-brand > img { display: block; width: 3.25rem; height: 2.5rem; object-fit: contain; }
.host-brand > div { display: flex; flex-direction: column; }
.host-brand strong { font-size: 0.875rem; letter-spacing: 0.12em; }
.host-brand small { color: #86948c; font-size: 0.75rem; }
.host-actions { display: flex; align-items: center; justify-content: flex-end; gap: 0.5rem; }
.host-pause-button { display: inline-flex; min-height: 3rem; align-items: center; gap: 0.4rem; padding: 0 0.8rem; border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 1rem; color: #dce5e0; background: rgba(255, 255, 255, 0.045); font-size: 0.75rem; font-weight: 850; transition: border-color 180ms ease, color 180ms ease, background 180ms ease; }
.host-pause-button .v-icon { color: currentColor; font-size: 1.25rem; }
.host-pause-button.active { border-color: rgba(88, 211, 136, 0.3); color: var(--green); background: rgba(88, 211, 136, 0.1); }
.host-pause-button:disabled { cursor: wait; opacity: 0.55; }
.connection-pill { display: inline-flex; align-items: center; gap: 0.4rem; min-height: 2.125rem; padding: 0 0.7rem; border: 1px solid rgba(88, 211, 136, 0.2); border-radius: 2rem; color: var(--green); background: rgba(88, 211, 136, 0.07); font-size: 0.75rem; font-weight: 850; }
.connection-pill i { width: 0.45rem; height: 0.45rem; border-radius: 50%; background: currentColor; }
.connection-pill.offline { border-color: rgba(255, 200, 75, 0.2); color: #f7c84b; background: rgba(247, 200, 75, 0.07); }
.host-error { position: fixed; z-index: 12; top: 5.5rem; left: 50%; display: flex; width: min(calc(100% - 2rem), 42rem); align-items: center; gap: 0.625rem; padding: 0.75rem 0.875rem; border: 1px solid rgba(255, 107, 122, 0.32); border-radius: 0.875rem; color: #ff9aa4; background: rgba(61, 18, 24, 0.96); translate: -50% 0; font-size: 0.875rem; }
.host-error span { flex: 1; }
.host-error .v-icon { color: currentColor; }
.host-error button { color: currentColor; }
.pause-requested-banner { position: relative; z-index: 4; display: grid; width: min(calc(100% - 2rem), 52rem); grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.75rem; margin: 1rem auto 0; padding: 0.75rem; border: 1px solid rgba(247, 200, 75, 0.3); border-radius: 1rem; color: #f3d57f; background: rgba(54, 43, 15, 0.92); box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.2); }
.pause-requested-banner > span { display: grid; width: 2.75rem; height: 2.75rem; place-items: center; border-radius: 0.875rem; background: rgba(247, 200, 75, 0.11); }
.pause-requested-banner .v-icon { color: currentColor; }
.pause-requested-banner > div { display: flex; min-width: 0; flex-direction: column; }
.pause-requested-banner strong { font-size: 0.875rem; }
.pause-requested-banner small { margin-top: 0.125rem; color: #c7b77e; font-size: 0.75rem; }
.pause-requested-banner button { min-height: 2.5rem; padding: 0 0.75rem; border: 1px solid rgba(247, 200, 75, 0.22); border-radius: 0.75rem; color: #f4d36f; font-size: 0.75rem; font-weight: 850; }

.host-stage,
.host-center-state { position: relative; z-index: 1; width: min(calc(100% - 2rem), 90rem); min-height: calc(100vh - 5rem); margin: 0 auto; padding: 2.5rem 0 4rem; }
.host-center-state { display: flex; align-items: center; justify-content: center; flex-direction: column; text-align: center; }
.host-center-state h1 { margin: 1.25rem 0 0.5rem; font-size: clamp(2rem, 5vw, 3.75rem); }
.host-center-state p { margin: 0 0 1.25rem; color: #96a39c; font-size: 1rem; }
.error-state > span { display: grid; width: 5rem; height: 5rem; place-items: center; border-radius: 1.5rem; background: rgba(255, 107, 122, 0.09); }
.error-state > span .v-icon { color: #ff7c89; font-size: 2rem; }
.host-kicker { display: inline-flex; align-items: center; gap: 0.5rem; color: var(--green); font-size: 0.75rem; font-weight: 900; letter-spacing: 0.16em; }
.host-kicker i { width: 0.5rem; height: 0.5rem; border-radius: 50%; background: currentColor; box-shadow: 0 0 0 0.35rem rgba(88, 211, 136, 0.1); }

.host-lobby { display: grid; grid-template-columns: minmax(0, 1.45fr) minmax(20rem, 0.55fr); align-items: center; gap: 2rem; }
.lobby-hero { text-align: center; }
.lobby-hero h1 { max-width: 60rem; margin: 1rem auto 0; font-size: clamp(3rem, 7vw, 6rem); line-height: 0.98; letter-spacing: -0.055em; }
.lobby-hero p { margin: 1.25rem 0 0.75rem; color: #9aa79f; font-size: 1rem; }
.lobby-hero p strong { color: #dce4df; }
.giant-code { color: var(--green); text-shadow: 0 0 3rem rgba(88, 211, 136, 0.22); font-size: clamp(4rem, 11vw, 9rem); font-weight: 950; letter-spacing: 0.08em; line-height: 1; }
.lobby-live-count { display: inline-flex; align-items: center; gap: 0.75rem; margin-top: 1.5rem; padding: 0.75rem 1rem; border: 1px solid rgba(88, 211, 136, 0.2); border-radius: 1rem; background: rgba(88, 211, 136, 0.06); }
.lobby-live-count span { color: var(--green); font-size: 1.75rem; font-weight: 950; }
.lobby-live-count small { color: #b8c2bc; font-size: 0.875rem; }
.host-participants { align-self: stretch; display: flex; min-height: 32rem; flex-direction: column; padding: 1.125rem; border: 1px solid var(--border); border-radius: 1.5rem; background: rgba(255, 255, 255, 0.035); }
.participants-heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; }
.participants-heading span { color: #91a098; font-size: 0.75rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
.participants-heading strong { display: grid; min-width: 2rem; height: 2rem; place-items: center; border-radius: 0.75rem; color: var(--green); background: rgba(88, 211, 136, 0.1); font-size: 0.875rem; }
.participant-list { display: grid; align-content: start; gap: 0.5rem; max-height: 23rem; overflow-y: auto; }
.participant-list > div { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.65rem; min-height: 3.25rem; padding: 0.45rem 0.625rem; border: 1px solid rgba(255, 255, 255, 0.07); border-radius: 0.875rem; background: rgba(0, 0, 0, 0.15); }
.participant-list > div > span { display: grid; width: 2.25rem; height: 2.25rem; place-items: center; border-radius: 0.75rem; color: #07100a; background: #8cdca9; font-size: 0.75rem; font-weight: 900; }
.participant-list strong { overflow: hidden; font-size: 0.875rem; text-overflow: ellipsis; white-space: nowrap; }
.participant-list i { width: 0.5rem; height: 0.5rem; border-radius: 50%; background: var(--green); }
.participant-list > div.offline { opacity: 0.45; }
.participant-list > div.offline i { background: #68756e; }
.participants-empty { display: flex; flex: 1; align-items: center; justify-content: center; flex-direction: column; color: #69776f; text-align: center; }
.participants-empty .v-icon { color: currentColor; font-size: 2.5rem; }
.participants-empty p { margin: 0.5rem 0 0; font-size: 0.875rem; }
.primary-host-action { min-height: 3.75rem !important; margin-top: auto; border-radius: 1rem !important; color: #07100a !important; font-size: 1rem !important; font-weight: 900 !important; letter-spacing: 0 !important; text-transform: none !important; }
.primary-host-action.compact { width: auto; min-width: 15rem; margin-top: 0; }
.start-hint { margin-top: 0.5rem; color: #7d8a83; font-size: 0.75rem; text-align: center; }

.host-countdown { display: flex; align-items: center; justify-content: center; flex-direction: column; text-align: center; }
.countdown-ring { display: grid; width: clamp(12rem, 28vw, 19rem); height: clamp(12rem, 28vw, 19rem); place-items: center; margin: 1.5rem 0; border: 0.15rem solid rgba(88, 211, 136, 0.3); border-radius: 50%; background: radial-gradient(circle, rgba(88, 211, 136, 0.17), transparent 68%); box-shadow: 0 0 7rem rgba(88, 211, 136, 0.13); animation: countdownPop 700ms ease both; }
.countdown-ring span { color: var(--green); font-size: clamp(7rem, 17vw, 12rem); font-weight: 950; line-height: 1; }
.host-countdown h1 { margin: 0; font-size: clamp(2rem, 5vw, 4rem); }

.host-question { padding-top: 1.5rem; }
.question-status-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; color: #95a39b; font-size: 0.875rem; font-weight: 800; }
.question-context,
.question-live-controls { display: flex; align-items: center; gap: 0.625rem; }
.question-context { flex-wrap: wrap; }
.question-live-controls { justify-content: flex-end; }
.question-answer-count { display: inline-flex; align-items: center; gap: 0.4rem; white-space: nowrap; }
.question-status-row .v-icon { color: var(--green); }
.question-type-pill { display: inline-flex; min-height: 1.875rem; align-items: center; gap: 0.35rem; padding: 0 0.625rem; border: 1px solid rgba(88, 211, 136, 0.18); border-radius: 2rem; color: #b8e7c9; background: rgba(88, 211, 136, 0.07); font-size: 0.75rem; }
.question-type-pill .v-icon { font-size: 1rem; }
.question-pause-action { display: inline-flex; min-height: 2.5rem; align-items: center; justify-content: center; gap: 0.45rem; padding: 0.45rem 0.75rem; border: 1px solid rgba(247, 200, 75, 0.28); border-radius: 0.875rem; color: #f3d77f; background: rgba(247, 200, 75, 0.07); font-family: inherit; font-size: 0.875rem; font-weight: 850; line-height: 1.25; text-align: center; cursor: pointer; transition: border-color 180ms ease, background 180ms ease, color 180ms ease, translate 180ms ease; }
.question-pause-action .v-icon { color: currentColor; font-size: 1.125rem; }
.question-pause-action:hover { border-color: rgba(247, 200, 75, 0.5); background: rgba(247, 200, 75, 0.12); translate: 0 -0.0625rem; }
.question-pause-action.active { border-color: rgba(88, 211, 136, 0.48); color: #a8efc0; background: rgba(88, 211, 136, 0.12); }
.question-pause-action:disabled { opacity: 0.5; cursor: default; translate: 0; }
.host-timer-track { height: 0.5rem; margin-top: 0.75rem; overflow: hidden; border-radius: 1rem; background: rgba(255, 255, 255, 0.07); }
.host-timer-track i { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg, #00a94f, #7cf0a9); transition: width 100ms linear; }
.host-question-title { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 2rem; min-height: 15rem; padding: 2rem 0; }
.host-question-title h1 { margin: 0; font-size: clamp(2.75rem, 6vw, 5rem); line-height: 1.08; letter-spacing: -0.045em; }
.host-timer { display: grid; width: 8rem; height: 8rem; place-items: center; align-content: center; border-radius: 50%; background: radial-gradient(circle at center, #111713 62%, transparent 64%), conic-gradient(var(--green) var(--timer-progress), rgba(255, 255, 255, 0.08) 0); }
.host-timer strong { font-size: 2.75rem; line-height: 1; }
.host-timer small { color: #87958d; font-size: 0.75rem; }
.host-answer-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.875rem; }
.host-answer { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; gap: 1rem; min-height: 7.5rem; padding: 1rem 1.25rem; border: 1px solid rgba(255, 255, 255, 0.16); border: 1px solid color-mix(in srgb, var(--answer-color), transparent 55%); border-radius: 1.25rem; background: rgba(255, 255, 255, 0.055); background: color-mix(in srgb, var(--answer-color), #0b0f0d 84%); }
.tone-coral { --answer-color: #ff5f68; }
.tone-blue { --answer-color: #49a3ff; }
.tone-amber { --answer-color: #f7c84b; }
.tone-violet { --answer-color: #b779ff; }
.host-answer > span,
.distribution-top > span { display: grid; width: 3.5rem; height: 3.5rem; place-items: center; border-radius: 1rem; background: rgba(255, 255, 255, 0.09); background: color-mix(in srgb, var(--answer-color), transparent 78%); }
.host-answer .v-icon,
.distribution-top > span .v-icon { color: var(--answer-color); }
.host-answer strong { font-size: 1.375rem; }
.host-estimate-prompt { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 1.25rem; min-height: 10rem; padding: 1.5rem; border: 1px solid rgba(73, 163, 255, 0.3); border-radius: 1.5rem; background: radial-gradient(circle at 0 50%, rgba(73, 163, 255, 0.16), transparent 36%), rgba(255, 255, 255, 0.035); }
.host-estimate-prompt > span { display: grid; width: 5rem; height: 5rem; place-items: center; border-radius: 1.25rem; background: rgba(73, 163, 255, 0.14); }
.host-estimate-prompt > span .v-icon { color: #71b6ff; font-size: 2.5rem; }
.host-estimate-prompt > div:not(.estimate-live-count) { display: flex; min-width: 0; flex-direction: column; }
.host-estimate-prompt small { color: #71b6ff; font-size: 0.75rem; font-weight: 900; letter-spacing: 0.12em; }
.host-estimate-prompt strong { margin-top: 0.35rem; font-size: 1.375rem; }
.host-estimate-prompt p { margin: 0.4rem 0 0; color: #98a69e; font-size: 0.875rem; line-height: 1.55; }
.estimate-live-count { display: flex; min-width: 8rem; align-items: center; flex-direction: column; padding: 0.875rem 1rem; border: 1px solid rgba(88, 211, 136, 0.2); border-radius: 1rem; background: rgba(88, 211, 136, 0.07); }
.estimate-live-count strong { margin: 0; color: var(--green); font-size: 2rem; line-height: 1; }
.estimate-live-count small { margin-top: 0.35rem; color: #a9b7af; font-size: 0.75rem; letter-spacing: 0; }
.question-guidance { display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin: 1rem 0 0; color: #a7b5ad; font-size: 0.875rem; }
.question-guidance .v-icon { color: #71b6ff; }
.auto-note { display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin: 1.25rem 0 0; color: #85928b; font-size: 0.875rem; }
.auto-note .v-icon { color: var(--green); }

.host-reveal { max-width: 76rem; }
.host-reveal > h1 { margin: 0.65rem 0 1.5rem; font-size: clamp(2.25rem, 5vw, 4rem); line-height: 1.12; }
.distribution-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; }
.distribution-card { padding: 1rem; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 1.125rem; background: rgba(255, 255, 255, 0.03); opacity: 0.55; }
.distribution-card.correct { border-color: rgba(88, 211, 136, 0.5); border-color: color-mix(in srgb, var(--answer-color), transparent 38%); background: rgba(88, 211, 136, 0.09); background: color-mix(in srgb, var(--answer-color), #0b0f0d 86%); opacity: 1; }
.distribution-top { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.75rem; }
.distribution-top > span { width: 3rem; height: 3rem; }
.distribution-top strong { font-size: 1.125rem; }
.correct-icon { color: var(--answer-color) !important; }
.distribution-bar { height: 0.5rem; margin-top: 0.875rem; overflow: hidden; border-radius: 1rem; background: rgba(255, 255, 255, 0.07); }
.distribution-bar i { display: block; height: 100%; border-radius: inherit; background: var(--answer-color); transition: width 500ms ease; }
.distribution-card > small { display: block; margin-top: 0.45rem; color: #8d9b93; font-size: 0.75rem; }
.estimate-reveal { display: grid; grid-template-columns: minmax(18rem, 0.8fr) minmax(0, 1.2fr); align-items: start; gap: 1rem; }
.estimate-target-card { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; gap: 1rem; min-height: 11rem; padding: 1.25rem; border: 1px solid rgba(88, 211, 136, 0.32); border-radius: 1.25rem; background: radial-gradient(circle at 0 0, rgba(88, 211, 136, 0.16), transparent 48%), rgba(255, 255, 255, 0.035); }
.estimate-target-card > span { display: grid; width: 4rem; height: 4rem; place-items: center; border-radius: 1.125rem; background: rgba(88, 211, 136, 0.13); }
.estimate-target-card > span .v-icon { color: var(--green); font-size: 2rem; }
.estimate-target-card > div { display: flex; min-width: 0; flex-direction: column; }
.estimate-target-card small { color: var(--green); font-size: 0.75rem; font-weight: 900; letter-spacing: 0.1em; }
.estimate-target-card strong { margin-top: 0.35rem; overflow-wrap: anywhere; font-size: clamp(2rem, 4vw, 3.5rem); line-height: 1; }
.estimate-target-card strong em { color: #a8b5ad; font-size: 1.125rem; font-style: normal; }
.estimate-target-card p { margin: 0.75rem 0 0; color: #9aa79f; font-size: 0.875rem; line-height: 1.5; }
.estimate-response-panel { padding: 1rem; border: 1px solid var(--border); border-radius: 1.25rem; background: rgba(255, 255, 255, 0.03); }
.estimate-response-heading { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 0.75rem; }
.estimate-response-heading > div { display: flex; flex-direction: column; }
.estimate-response-heading span { font-size: 1rem; font-weight: 900; }
.estimate-response-heading small { color: #829087; font-size: 0.75rem; }
.estimate-response-heading > strong { display: grid; min-width: 2.25rem; height: 2.25rem; place-items: center; border-radius: 0.75rem; color: var(--green); background: rgba(88, 211, 136, 0.1); font-size: 0.875rem; }
.estimate-response-list { display: grid; max-height: 24rem; gap: 0.5rem; overflow-y: auto; }
.estimate-response-list > div { display: grid; grid-template-columns: 2rem 2.5rem minmax(0, 1fr) auto 4.75rem; align-items: center; gap: 0.625rem; min-height: 4rem; padding: 0.5rem 0.625rem; border: 1px solid rgba(255, 255, 255, 0.07); border-radius: 0.875rem; background: rgba(0, 0, 0, 0.14); }
.estimate-response-list > div.winner { border-color: rgba(88, 211, 136, 0.35); background: rgba(88, 211, 136, 0.07); }
.estimate-rank { color: #76847c; font-size: 0.875rem; font-weight: 900; text-align: center; }
.estimate-avatar { display: grid; width: 2.5rem; height: 2.5rem; place-items: center; border-radius: 0.75rem; color: #07100a; background: #9aaba2; font-size: 0.75rem; font-weight: 900; }
.winner .estimate-avatar { background: var(--green); }
.estimate-person { display: flex; min-width: 0; flex-direction: column; }
.estimate-person strong { overflow: hidden; font-size: 0.875rem; text-overflow: ellipsis; white-space: nowrap; }
.estimate-person small { overflow: hidden; color: #87958d; font-size: 0.75rem; text-overflow: ellipsis; white-space: nowrap; }
.estimate-value { max-width: 12rem; overflow: hidden; font-size: 1rem; text-align: right; text-overflow: ellipsis; white-space: nowrap; }
.estimate-value small { color: #8e9b94; font-size: 0.75rem; }
.estimate-points { min-width: 4.75rem; color: var(--green); font-size: 0.875rem; font-weight: 950; text-align: right; }
.estimate-points::after { content: " Pkt."; color: #75827b; font-size: 0.75rem; font-weight: 800; }
.estimate-more { padding: 0.375rem 0.625rem; color: #839088; font-size: 0.75rem; text-align: center; }
.estimate-empty { display: flex; min-height: 10rem; align-items: center; justify-content: center; flex-direction: column; gap: 0.5rem; color: #78867e; font-size: 0.875rem; text-align: center; }
.estimate-empty .v-icon { color: currentColor; font-size: 2rem; }
.host-bottom-action { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-top: 1.25rem; padding: 1rem; border: 1px solid var(--border); border-radius: 1rem; background: rgba(255, 255, 255, 0.035); }
.host-bottom-action > span { color: #9aa79f; font-size: 0.875rem; }

.host-leaderboard { display: flex; max-width: 65rem; align-items: center; flex-direction: column; text-align: center; }
.host-leaderboard > h1,
.host-finished > h1 { margin: 0.5rem 0 1.5rem; font-size: clamp(3rem, 7vw, 5.5rem); letter-spacing: -0.055em; }
.host-ranking { display: grid; width: 100%; gap: 0.5rem; text-align: left; }
.host-ranking > div { display: grid; grid-template-columns: 2.75rem 3rem minmax(0, 1fr) auto auto; align-items: center; gap: 0.75rem; min-height: 4.75rem; padding: 0.625rem 1rem; border: 1px solid var(--border); border-radius: 1rem; background: rgba(255, 255, 255, 0.035); }
.rank-number { color: #7c8a82; font-size: 1.25rem; font-weight: 950; }
.rank-avatar { display: grid; width: 2.75rem; height: 2.75rem; place-items: center; border-radius: 0.875rem; color: #07100a; background: #8ba097; font-size: 0.75rem; font-weight: 900; }
.host-ranking strong { overflow: hidden; font-size: 1.125rem; text-overflow: ellipsis; white-space: nowrap; }
.host-ranking small { color: #86948c; font-size: 0.75rem; }
.rank-score { min-width: 5rem; color: var(--green); font-size: 1.125rem; font-weight: 950; text-align: right; }
.host-ranking .rank-1 { border-color: rgba(247, 200, 75, 0.38); background: rgba(247, 200, 75, 0.07); }
.host-ranking .rank-1 .rank-avatar { background: #f7c84b; }
.host-ranking .rank-2 .rank-avatar { background: #c9d0cc; }
.host-ranking .rank-3 .rank-avatar { background: #d28f61; }
.leaderboard-actions { display: flex; width: 100%; align-items: center; justify-content: center; gap: 0.75rem; margin-top: 1.25rem; }
.leaderboard-pause-action { display: inline-flex; min-width: 12rem; min-height: 3.75rem; align-items: center; justify-content: center; gap: 0.5rem; padding: 0.75rem 1rem; border: 1px solid rgba(247, 200, 75, 0.34); border-radius: 1rem; color: #f3d77f; background: rgba(247, 200, 75, 0.07); font-family: inherit; font-size: 1rem; font-weight: 850; line-height: 1.25; cursor: pointer; transition: border-color 180ms ease, background 180ms ease, translate 180ms ease; }
.leaderboard-pause-action .v-icon { color: currentColor; font-size: 1.25rem; }
.leaderboard-pause-action:hover { border-color: rgba(247, 200, 75, 0.58); background: rgba(247, 200, 75, 0.13); translate: 0 -0.0625rem; }
.leaderboard-pause-action:disabled { opacity: 0.5; cursor: default; translate: 0; }
.ranking-action { margin-top: 0 !important; }

.host-paused { display: flex; max-width: 68rem; align-items: center; justify-content: center; }
.paused-card { display: flex; width: 100%; align-items: center; flex-direction: column; padding: clamp(1.5rem, 4vw, 3rem); border: 1px solid rgba(88, 211, 136, 0.22); border-radius: 2rem; background: radial-gradient(circle at 50% 0, rgba(88, 211, 136, 0.14), transparent 48%), rgba(255, 255, 255, 0.035); box-shadow: 0 2rem 6rem rgba(0, 0, 0, 0.28); text-align: center; }
.paused-icon { display: grid; width: 5.5rem; height: 5.5rem; place-items: center; margin-bottom: 1.25rem; border: 1px solid rgba(88, 211, 136, 0.28); border-radius: 1.75rem; color: var(--green); background: rgba(88, 211, 136, 0.12); box-shadow: 0 0 3rem rgba(88, 211, 136, 0.12); }
.paused-icon .v-icon { color: currentColor; font-size: 2.75rem; }
.paused-card > h1 { margin: 0.625rem 0 0; font-size: clamp(2.5rem, 6vw, 4.75rem); line-height: 1; letter-spacing: -0.05em; }
.paused-card > p { max-width: 44rem; margin: 1rem auto 0; color: #9caaa2; font-size: 1rem; line-height: 1.6; }
.paused-facts { display: grid; width: 100%; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.75rem; margin-top: 2rem; text-align: left; }
.paused-facts > div { display: grid; grid-template-columns: auto minmax(0, 1fr); grid-template-rows: auto auto; column-gap: 0.75rem; padding: 1rem; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 1rem; background: rgba(0, 0, 0, 0.15); }
.paused-facts > div > span { display: grid; width: 2.75rem; height: 2.75rem; grid-row: 1 / span 2; place-items: center; border-radius: 0.875rem; color: var(--green); background: rgba(88, 211, 136, 0.09); }
.paused-facts .v-icon { color: currentColor; }
.paused-facts small { color: #829087; font-size: 0.75rem; }
.paused-facts strong { margin-top: 0.125rem; overflow-wrap: anywhere; font-size: 0.875rem; }
.paused-actions { display: flex; align-items: center; justify-content: center; gap: 0.75rem; margin-top: 1.5rem; }
.paused-secondary-action { min-height: 3.75rem !important; border-radius: 1rem !important; font-size: 1rem !important; font-weight: 850 !important; letter-spacing: 0 !important; text-transform: none !important; }

.host-finished { display: flex; max-width: 70rem; align-items: center; flex-direction: column; text-align: center; }
.host-podium { display: flex; width: 100%; min-height: 25rem; align-items: flex-end; justify-content: center; gap: 0.75rem; }
.host-podium-place { position: relative; display: flex; width: min(30%, 17rem); align-items: center; flex-direction: column; padding: 1.25rem 0.75rem 0; border: 1px solid var(--podium-border); border-bottom: 0; border-radius: 1.5rem 1.5rem 0 0; background: var(--podium-bg); }
.host-podium-place.place-one { --podium-border: rgba(247, 200, 75, 0.5); --podium-bg: linear-gradient(180deg, rgba(247, 200, 75, 0.17), rgba(247, 200, 75, 0.04)); min-height: 23rem; }
.host-podium-place.place-two { --podium-border: rgba(205, 216, 210, 0.35); --podium-bg: linear-gradient(180deg, rgba(205, 216, 210, 0.12), rgba(205, 216, 210, 0.03)); min-height: 18rem; }
.host-podium-place.place-three { --podium-border: rgba(210, 143, 97, 0.4); --podium-bg: linear-gradient(180deg, rgba(210, 143, 97, 0.14), rgba(210, 143, 97, 0.03)); min-height: 15rem; }
.host-podium-place > span { display: grid; width: 5rem; height: 5rem; place-items: center; border-radius: 1.5rem; color: #07100a; background: #d7e0db; font-size: 1.25rem; font-weight: 950; }
.host-podium-place.place-one > span { background: #f7c84b; }
.host-podium-place.place-three > span { background: #d28f61; }
.host-podium-place strong { width: 100%; margin-top: 0.875rem; overflow: hidden; font-size: 1rem; text-overflow: ellipsis; white-space: nowrap; }
.host-podium-place small { margin-top: 0.25rem; color: #a7b3ac; font-size: 0.875rem; }
.host-podium-place > i { margin-top: auto; padding-bottom: 1rem; color: rgba(255, 255, 255, 0.2); font-size: 4rem; font-style: normal; font-weight: 950; }
.host-podium-place > .v-icon { position: absolute; top: -3rem; color: #f7c84b; font-size: 2.75rem; }
.host-podium.single-winner .place-one { width: min(70%, 18rem); }

@keyframes countdownPop { 0% { opacity: 0; } 35%, 100% { opacity: 1; } }

@media (max-width: 59.9375rem) {
  .host-lobby { grid-template-columns: 1fr; padding-top: 1.5rem; }
  .host-participants { min-height: 20rem; }
  .participant-list { max-height: 15rem; }
  .estimate-reveal { grid-template-columns: 1fr; }
}

@media (max-width: 47.9375rem) {
  .quiz-host-page { height: 100vh; height: 100dvh; }
  .host-header { position: sticky; top: 0; grid-template-columns: 2.75rem minmax(0, 1fr) auto; min-height: 4.25rem; padding: calc(0.5rem + env(safe-area-inset-top)) 0.625rem 0.5rem; padding-right: max(0.625rem, env(safe-area-inset-right)); padding-left: max(0.625rem, env(safe-area-inset-left)); border-bottom-color: rgba(255, 255, 255, 0.09); background: rgba(8, 11, 10, 0.92); box-shadow: 0 0.75rem 2rem rgba(0, 0, 0, 0.18); -webkit-backdrop-filter: blur(1.25rem); }
  .host-icon-button { width: 2.75rem; height: 2.75rem; border-radius: 0.875rem; touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
  .host-icon-button:active { translate: 0 0.0625rem; opacity: 0.82; }
  .host-brand { min-width: 0; justify-self: start; gap: 0.5rem; margin-left: 0.375rem; }
  .host-brand > img { width: 2.75rem; height: 2.125rem; }
  .host-actions .host-icon-button:last-child { display: none; }
  .host-brand > div { min-width: 0; }
  .host-brand strong { overflow: hidden; font-size: 0.75rem; text-overflow: ellipsis; white-space: nowrap; }
  .host-brand small { font-size: 0.75rem; }
  .host-actions { gap: 0.375rem; }
  .host-pause-button { width: 2.75rem; min-height: 2.75rem; justify-content: center; padding: 0; border-radius: 0.875rem; touch-action: manipulation; }
  .host-pause-button > span { display: none; }
  .connection-pill { min-height: 2rem; padding: 0 0.55rem; }
  .host-error { top: calc(4.75rem + env(safe-area-inset-top)); right: max(0.5rem, env(safe-area-inset-right)); left: max(0.5rem, env(safe-area-inset-left)); width: auto; border-radius: 1rem; translate: 0; }
  .pause-requested-banner { width: auto; grid-template-columns: auto minmax(0, 1fr); margin: 0.75rem max(0.5rem, env(safe-area-inset-right)) 0 max(0.5rem, env(safe-area-inset-left)); }
  .pause-requested-banner button { grid-column: 1 / -1; width: 100%; }
  .host-stage,
  .host-center-state { width: auto; min-height: calc(100vh - 4.25rem); min-height: calc(100dvh - 4.25rem - env(safe-area-inset-top)); margin-right: max(0.5rem, env(safe-area-inset-right)); margin-left: max(0.5rem, env(safe-area-inset-left)); padding: 1rem 0 calc(2rem + env(safe-area-inset-bottom)); }
  .host-kicker { letter-spacing: 0.11em; }

  .host-lobby { align-items: start; gap: 1rem; padding-top: 1rem; }
  .lobby-hero { padding: 1.25rem 0.875rem; border: 1px solid rgba(88, 211, 136, 0.16); border-radius: 1.5rem; background: radial-gradient(circle at 50% 0, rgba(88, 211, 136, 0.12), transparent 58%), rgba(255, 255, 255, 0.025); }
  .lobby-hero h1 { margin-top: 0.75rem; overflow-wrap: anywhere; font-size: clamp(1.75rem, 8vw, 2.75rem); line-height: 1.08; letter-spacing: -0.035em; }
  .lobby-hero p { margin: 0.875rem 0 0.625rem; font-size: 0.875rem; line-height: 1.5; }
  .giant-code { overflow-wrap: anywhere; font-size: clamp(3.25rem, 17vw, 5rem); letter-spacing: 0.04em; }
  .lobby-live-count { margin-top: 1rem; padding: 0.625rem 0.875rem; }
  .lobby-live-count span { font-size: 1.5rem; }
  .host-participants { min-height: 0; padding: 0.875rem; border-radius: 1.25rem; }
  .participant-list { max-height: 15rem; }
  .participants-empty { min-height: 8rem; }
  .primary-host-action { min-height: 3.5rem !important; border-radius: 0.875rem !important; touch-action: manipulation; }

  .host-countdown { justify-content: flex-start; padding-top: clamp(3rem, 14vh, 7rem); }
  .countdown-ring { width: clamp(10rem, 48vw, 14rem); height: clamp(10rem, 48vw, 14rem); margin: 1.25rem 0; }
  .countdown-ring span { font-size: clamp(5rem, 24vw, 7rem); }
  .host-countdown h1 { font-size: clamp(2rem, 9vw, 3rem); }

  .host-question { padding-top: 0.875rem; }
  .question-status-row { align-items: center; flex-wrap: wrap; gap: 0.5rem; }
  .question-context { gap: 0.4rem; }
  .question-live-controls { width: 100%; align-items: stretch; justify-content: space-between; gap: 0.5rem; }
  .question-answer-count { min-height: 2.75rem; padding: 0.5rem 0.7rem; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 0.875rem; background: rgba(255, 255, 255, 0.035); font-size: 0.75rem; }
  .question-pause-action { min-height: 2.75rem; flex: 1; padding: 0.5rem 0.75rem; touch-action: manipulation; }
  .question-pause-action:active { translate: 0 0.0625rem; }
  .host-timer-track { height: 0.375rem; margin-top: 0.625rem; }
  .host-question-title { grid-template-columns: minmax(0, 1fr) 4rem; align-items: start; min-height: 0; gap: 0.75rem; padding: 1.25rem 0 1rem; }
  .host-question-title h1 { overflow-wrap: anywhere; hyphens: auto; font-size: clamp(1.75rem, 7.5vw, 2.5rem); line-height: 1.12; letter-spacing: -0.03em; }
  .host-timer { width: 4rem; height: 4rem; }
  .host-timer strong { font-size: 1.375rem; }
  .host-answer-grid,
  .distribution-grid { grid-template-columns: 1fr; gap: 0.625rem; }
  .host-answer { min-height: 4.75rem; gap: 0.75rem; padding: 0.75rem 0.875rem; border-radius: 1.125rem; }
  .host-answer > span { width: 2.75rem; height: 2.75rem; border-radius: 0.875rem; }
  .host-answer strong { overflow-wrap: anywhere; font-size: 1rem; line-height: 1.4; }
  .host-estimate-prompt { grid-template-columns: auto minmax(0, 1fr); gap: 0.875rem; min-height: 0; padding: 0.875rem; border-radius: 1.125rem; }
  .host-estimate-prompt > span { width: 3rem; height: 3rem; border-radius: 0.875rem; }
  .host-estimate-prompt > span .v-icon { font-size: 1.5rem; }
  .host-estimate-prompt strong { font-size: 1rem; line-height: 1.4; }
  .host-estimate-prompt p { font-size: 0.875rem; }
  .estimate-live-count { grid-column: 1 / -1; width: 100%; flex-direction: row; justify-content: center; gap: 0.5rem; padding: 0.75rem; }
  .estimate-live-count strong { font-size: 1.5rem; }
  .estimate-live-count small { margin: 0; }
  .question-guidance,
  .auto-note { justify-content: flex-start; margin-top: 0.75rem; padding: 0.625rem 0.75rem; border: 1px solid rgba(255, 255, 255, 0.07); border-radius: 0.875rem; background: rgba(255, 255, 255, 0.025); line-height: 1.45; }

  .host-reveal { padding-top: 1rem; }
  .host-reveal > h1 { margin: 0.5rem 0 1rem; overflow-wrap: anywhere; hyphens: auto; font-size: clamp(1.75rem, 7.5vw, 2.5rem); line-height: 1.12; letter-spacing: -0.03em; }
  .distribution-card { padding: 0.875rem; border-radius: 1rem; }
  .distribution-top { gap: 0.625rem; }
  .distribution-top > span { width: 2.75rem; height: 2.75rem; border-radius: 0.875rem; }
  .distribution-top strong { overflow-wrap: anywhere; font-size: 1rem; line-height: 1.4; }
  .distribution-bar { margin-top: 0.75rem; }
  .estimate-reveal { gap: 0.75rem; }
  .estimate-target-card { grid-template-columns: auto minmax(0, 1fr); min-height: 0; gap: 0.875rem; padding: 1rem; border-radius: 1.125rem; text-align: left; }
  .estimate-target-card > span { width: 3rem; height: 3rem; margin: 0; border-radius: 0.875rem; }
  .estimate-target-card > span .v-icon { font-size: 1.5rem; }
  .estimate-target-card strong { font-size: clamp(1.75rem, 8vw, 2.5rem); }
  .estimate-target-card p { margin-top: 0.5rem; }
  .estimate-response-panel { padding: 0.75rem; border-radius: 1.125rem; }
  .estimate-response-list { max-height: none; }
  .estimate-response-list > div { grid-template-columns: 1.75rem 2.5rem minmax(0, 1fr) auto; }
  .estimate-value { grid-column: 3 / 4; max-width: none; text-align: left; }
  .estimate-points { grid-column: 4 / 5; grid-row: 1 / 3; }
  .host-bottom-action { position: sticky; z-index: 4; bottom: calc(0.625rem + env(safe-area-inset-bottom)); align-items: stretch; flex-direction: column; gap: 0.75rem; margin-top: 1rem; padding: 0.75rem; border-color: rgba(88, 211, 136, 0.2); border-radius: 1.125rem; background: rgba(14, 20, 17, 0.94); box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.45); -webkit-backdrop-filter: blur(1.25rem); backdrop-filter: blur(1.25rem); }
  .host-bottom-action > span { text-align: center; }
  .primary-host-action.compact { width: 100%; min-width: 0; }

  .host-leaderboard,
  .host-finished { align-items: stretch; text-align: left; }
  .host-leaderboard > h1,
  .host-finished > h1 { margin: 0.35rem 0 1rem; font-size: clamp(2.25rem, 10vw, 3rem); letter-spacing: -0.04em; }
  .host-ranking { gap: 0.4rem; }
  .host-ranking > div { grid-template-columns: 1.75rem 2.5rem minmax(0, 1fr) auto; gap: 0.5rem; min-height: 4rem; padding: 0.5rem 0.75rem; border-radius: 0.875rem; }
  .host-ranking small { display: none; }
  .rank-number { font-size: 1rem; }
  .rank-avatar { width: 2.5rem; height: 2.5rem; border-radius: 0.75rem; }
  .host-ranking strong { font-size: 1rem; }
  .rank-score { min-width: 3.75rem; font-size: 1rem; }
  .leaderboard-actions { position: sticky; z-index: 4; bottom: calc(0.625rem + env(safe-area-inset-bottom)); align-items: stretch; flex-direction: column; gap: 0.5rem; margin-top: 1rem; padding: 0.625rem; border: 1px solid rgba(88, 211, 136, 0.18); border-radius: 1.125rem; background: rgba(14, 20, 17, 0.94); box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.45); -webkit-backdrop-filter: blur(1.25rem); backdrop-filter: blur(1.25rem); }
  .leaderboard-pause-action,
  .leaderboard-actions .ranking-action { width: 100%; min-width: 0; }
  .leaderboard-actions .ranking-action { box-shadow: none; }
  .host-finished > .primary-host-action { position: sticky; z-index: 4; bottom: calc(0.625rem + env(safe-area-inset-bottom)); margin-top: 1rem !important; box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.45); }

  .host-paused { align-items: flex-start; }
  .paused-card { padding: 1.25rem 0.875rem; border-radius: 1.5rem; }
  .paused-icon { width: 4rem; height: 4rem; margin-bottom: 1rem; border-radius: 1.25rem; }
  .paused-icon .v-icon { font-size: 2rem; }
  .paused-card > h1 { font-size: clamp(2rem, 10vw, 3rem); }
  .paused-card > p { font-size: 0.875rem; line-height: 1.55; }
  .paused-facts { grid-template-columns: 1fr; gap: 0.5rem; margin-top: 1.25rem; }
  .paused-facts > div { padding: 0.75rem; }
  .paused-actions { width: 100%; align-items: stretch; flex-direction: column; }
  .paused-secondary-action { width: 100%; }

  .host-podium { display: grid; min-height: 0; grid-template-columns: 1fr; align-items: stretch; gap: 0.625rem; margin-top: 0.5rem; }
  .host-podium-place { display: grid; width: 100%; min-height: 0 !important; grid-template-columns: 3rem minmax(0, 1fr) auto; grid-template-rows: auto auto; align-items: center; column-gap: 0.75rem; padding: 0.75rem; border-bottom: 1px solid var(--podium-border); border-radius: 1.125rem; text-align: left; }
  .host-podium-place.place-one { order: 1; }
  .host-podium-place.place-two { order: 2; }
  .host-podium-place.place-three { order: 3; }
  .host-podium-place > span { grid-column: 1; grid-row: 1 / span 2; width: 3rem; height: 3rem; border-radius: 0.875rem; font-size: 0.875rem; }
  .host-podium-place strong { grid-column: 2; grid-row: 1; margin-top: 0; font-size: 1rem; }
  .host-podium-place small { grid-column: 2; grid-row: 2; margin-top: 0.125rem; font-size: 0.875rem; }
  .host-podium-place > i { grid-column: 3; grid-row: 1 / span 2; margin: 0; padding: 0; font-size: 1.75rem; }
  .host-podium-place > .v-icon { top: 0.625rem; right: 3rem; font-size: 1.25rem; }
  .host-podium.single-winner .place-one { width: 100%; }
}

@media (max-width: 25rem) {
  .connection-pill { display: none; }
  .host-brand small { display: none; }
  .question-live-controls { display: grid; grid-template-columns: 1fr; }
  .question-answer-count,
  .question-pause-action { width: 100%; justify-content: center; }
  .estimate-avatar { display: none; }
  .estimate-response-list > div { grid-template-columns: 1.5rem minmax(0, 1fr) auto; }
  .estimate-value { grid-column: 2 / 3; }
  .estimate-points { grid-column: 3 / 4; }
}

@media (max-height: 40rem) and (max-width: 59.9375rem) {
  .host-stage,
  .host-center-state { padding-top: 0.75rem; }
  .host-countdown { padding-top: 1.5rem; }
  .countdown-ring { width: 9rem; height: 9rem; margin: 0.75rem 0; }
  .countdown-ring span { font-size: 4.5rem; }
}

@media (prefers-reduced-motion: reduce) {
  .countdown-ring { animation: none; }
}
</style>
